import React, { useEffect, useState } from 'react';
import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';
import Button from 'views/Button.js';
import PageLayout from 'views/PageLayout.js';
import Request from 'files/Request.js';
import Analytics from 'files/Analytics.js';
import Captcha from 'files/Captcha.js';

// Posts to the aftplatform API — backend endpoint: POST {config.url}/users/
// Payload: { type: 'new_shopify_contact_form', full_name, email_address, message, captcha_token }
const ContactPage = ({ utils }) => {

    const [form, setForm] = useState({ name: '', email: '', message: '' });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    // start loading recaptcha as soon as the page is shown
    useEffect(() => {
        Captcha.load().catch(() => {});
    }, []);

    const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if(submitting) {
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await Request.post(utils, '/users/', {
                captcha_token: await Captcha.getToken('contact'),
                full_name: form.name,
                email_address: form.email,
                message: form.message,
                type: 'new_shopify_contact_form',
            });
            setSubmitted(true);
            Analytics.contactSubmit({ form: 'contact_page' });
        } catch(err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const fieldStyle = {
        fontFamily: Appearance.fonts.body,
        fontSize: 15,
        padding: '13px 16px',
        borderRadius: 12,
        border: `1px solid ${Appearance.colors.border()}`,
        backgroundColor: Appearance.colors.white,
        color: Appearance.colors.text(),
        outline: 'none',
        width: '100%',
    };
    const labelStyle = { ...Appearance.textStyles.caption({ color: Appearance.colors.text(), fontWeight: 600 }), display: 'block', marginBottom: 8 };

    return (
        <PageLayout
            eyebrow={'Contact'}
            title={'Get in touch'}
            subtitle={'Have a question? Send us a message and our U.S.-based team will get back to you shortly.'}
            maxWidth={640}>

            <div style={{ maxWidth: 560, margin: '0 auto' }}>
                {submitted ? (
                    <div style={{
                        textAlign: 'center',
                        padding: Utils.isMobile() ? '48px 24px' : '56px 40px',
                        borderRadius: 20,
                        backgroundColor: Appearance.colors.offWhite,
                    }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            backgroundColor: Utils.hexToRGBA(Appearance.colors.success, 0.15),
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px',
                        }}>
                            <i className={'fas fa-check'} style={{ fontSize: 22, color: Appearance.colors.success }} />
                        </div>
                        <h3 style={{ ...Appearance.textStyles.heading({ fontSize: 24 }), marginBottom: 10 }}>{'Message sent'}</h3>
                        <p style={Appearance.textStyles.body()}>{'Thanks for reaching out — we’ll get back to you shortly.'}</p>
                    </div>
                ) : (
                    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: Utils.isMobile() ? '1fr' : '1fr 1fr', gap: 18 }}>
                            <div>
                                <label style={labelStyle}>{'Name'}</label>
                                <input type={'text'} value={form.name} onChange={update('name')} required={true} style={fieldStyle} />
                            </div>
                            <div>
                                <label style={labelStyle}>{'Email'}</label>
                                <input type={'email'} value={form.email} onChange={update('email')} required={true} style={fieldStyle} />
                            </div>
                        </div>
                        <div>
                            <label style={labelStyle}>{'How can we help?'}</label>
                            <textarea value={form.message} onChange={update('message')} rows={5} required={true} style={{ ...fieldStyle, resize: 'vertical' }} />
                        </div>

                        {error && (
                            <span style={Appearance.textStyles.caption({ color: Appearance.colors.error })}>{error}</span>
                        )}

                        <div style={{ marginTop: 4 }}>
                            <Button label={'Send message'} type={'primary'} loading={submitting} onClick={() => {}} style={{ padding: '15px 40px', fontSize: 16 }} />
                        </div>
                    </form>
                )}
            </div>
        </PageLayout>
    );
};

export default ContactPage;
